import React from 'react';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  disabled?: boolean;
}

const Pagination: React.FC<PaginationProps> = ({ currentPage, totalPages, onPageChange, disabled = false }) => {
  if (totalPages <= 1) return null;
  
  const maxPageButtons = 5; // Desktop
  const maxMobilePageButtons = 3; // Mobile
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;
  const activeMaxButtons = isMobile ? maxMobilePageButtons : maxPageButtons;
  
  const pageNumbers: number[] = [];
  
  if (totalPages <= activeMaxButtons) {
    // Show all pages if they fit
    for (let i = 1; i <= totalPages; i++) {
      pageNumbers.push(i);
    }
  } else {
    // Always show first page, last page, and some around current page
    pageNumbers.push(1);
    
    let startPage = Math.max(2, currentPage - (isMobile ? 0 : 1));
    let endPage = Math.min(totalPages - 1, currentPage + (isMobile ? 0 : 1));
    
    // Ensure we have enough pages in the middle section
    if (endPage - startPage < (isMobile ? 0 : 2)) {
      if (startPage === 2) {
        endPage = Math.min(totalPages - 1, startPage + (isMobile ? 0 : 2));
      } else if (endPage === totalPages - 1) {
        startPage = Math.max(2, endPage - (isMobile ? 0 : 2));
      }
    }
    
    // Add ellipsis if needed
    if (startPage > 2) {
      pageNumbers.push(-1); // -1 represents ellipsis
    }
    
    // Add middle pages
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }
    
    // Add ellipsis if needed
    if (endPage < totalPages - 1) {
      pageNumbers.push(-2); // -2 represents ellipsis
    }
    
    // Add last page
    pageNumbers.push(totalPages);
  }
  
  return (
    <nav className="flex justify-center my-4" aria-label="Pagination">
      <ul className="inline-flex flex-wrap items-center justify-center gap-1 sm:gap-0">
        <li>
          <button
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1 || disabled}
            className="px-2 py-1 sm:px-3 sm:py-1 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed min-w-[60px] sm:min-w-[80px] flex justify-center items-center"
            aria-label="Previous page"
          >
            <span className="sm:hidden">&lsaquo;</span>
            <span className="hidden sm:inline">Previous</span>
          </button>
        </li>

        {pageNumbers.map((page, index) => {
          if (page < 0) {
            return (
              <li key={`ellipsis-${index}`} className="hidden sm:block">
                <span className="px-3 py-1 border border-gray-300 bg-white text-sm text-gray-700">
                  ...
                </span>
              </li>
            );
          }

          return (
            <li key={page}>
              <button
                onClick={() => onPageChange(page)}
                disabled={disabled}
                className={`w-8 h-8 sm:w-auto sm:h-auto sm:px-3 sm:py-1 border border-gray-300 text-sm font-medium flex items-center justify-center disabled:cursor-not-allowed disabled:opacity-60 ${
                  currentPage === page
                    ? 'bg-primary text-white hover:bg-primary/90'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
                aria-label={`Page ${page}`}
                aria-current={currentPage === page ? 'page' : undefined}
              >
                {page}
              </button>
            </li>
          );
        })}

        <li>
          <button
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages || disabled}
            className="px-2 py-1 sm:px-3 sm:py-1 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed min-w-[60px] sm:min-w-[80px] flex justify-center items-center"
            aria-label="Next page"
          >
            <span className="sm:hidden">&rsaquo;</span>
            <span className="hidden sm:inline">Next</span>
          </button>
        </li>
      </ul>
    </nav>
  );
};

export default Pagination; 